import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Rocket, Mail, Lock, User as UserIcon, Loader2,
  AlertCircle, Check, X, Sparkles, ArrowRight,
} from "lucide-react";
import toast from "react-hot-toast";
import { authedFetch, API_BASE } from "../utils/authedFetch";
import { signInSuccess } from "../store/slices/user.slice";
import { clearDemoMembership } from "../utils/demoMembership";
import { track } from "../utils/analytics";

/**
 * DemoConvertModal — the conversion lever for demo accounts.
 *
 * When a demo visitor clicks "Pick a plan to keep going" in the
 * DemoBanner, this opens instead of dumping them on /pricing
 * (which is hostile and high-friction). It asks them to confirm
 * the email + password they used at signup, calls POST
 * /api/v1/demo/convert, then redirects to /pricing.
 *
 * Why a modal and not a full page: the visitor is mid-tour,
 * still inside a working dashboard. Yanking them to a full
 * signup page would lose the context. A modal keeps the
 * product visible behind it.
 *
 * The demo is a *tour* of static, fake sample data — not the
 * visitor's own records. So conversion provisions a brand-new,
 * clean CRM; nothing from the demo carries over (there's no
 * real data of theirs to carry). The copy and the backend
 * payload (keepSampleData:false, always) reflect that. See the
 * backend follow-up below — /demo/convert must provision a
 * fresh CRM rather than re-skin the demo's seeded data.
 *
 * ── Architectural follow-up (backend) ──
 * Under the gated-demo flow, every demo visitor signed up for
 * a real account before /demo/start was called — so they
 * already have an email + password on file. Asking for them
 * here is technically redundant. We can't drop the fields
 * without backend work, since /demo/convert currently uses
 * them to bind the ephemeral demo user to a real account
 * record. The clean fix is for /demo/convert to detect that
 * the demo's parent signup user is already in the database
 * and skip account creation, going straight to plan-pick +
 * payment. Until that lands, the modal copy is framed as
 * "confirm your details" so the visitor doesn't feel like
 * they're signing up twice.
 */

// Read the email DemoLauncher stashed at signup, so the modal can
// prefill it instead of asking the visitor to retype an address
// they already gave us. Returns "" if sessionStorage is blocked or
// the value is missing — falls through to a normal empty input.
/**
 * Which CRM the visitor was touring, for funnel attribution.
 *
 * Read off the stored user rather than a prop: this modal is rendered from
 * several places and none of them pass the vertical down, and the demo user
 * always carries its plan category.
 */
function readDemoVertical() {
  try {
    const u = JSON.parse(localStorage.getItem("__user__") || "{}");
    return u?.plan?.category || "unknown";
  } catch {
    return "unknown";
  }
}

function readSignupEmail() {
  try { return sessionStorage.getItem("__demo_signup_email__") || ""; }
  catch { return ""; }
}

export default function DemoConvertModal({ open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((s) => s.users?.user);
  // Gated-demo path: the demo record carries parentSignupUserId so
  // /demo/convert can promote the parent without needing credentials.
  // When present we render a stripped-down "pick a plan" UI instead
  // of the legacy email/password form.
  const hasParentSignup = Boolean(currentUser?.parentSignupUserId);
  const [email, setEmail] = useState(readSignupEmail);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // Focus the right field when the modal opens. Gated-flow path
  // renders no credential fields at all (parent account already
  // has them) so we skip autofocus entirely — the submit button
  // catches focus on Enter. Legacy path: focus password if the
  // email is already prefilled from sessionStorage, otherwise
  // focus email.
  useEffect(() => {
    if (open) {
      if (!hasParentSignup) {
        const target = email ? passwordRef.current : emailRef.current;
        if (target) {
          // Defer one frame so the autofocus doesn't fight CSS transitions.
          requestAnimationFrame(() => target.focus());
        }
      }
      setError(null);
      track("convert_modal_opened");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Esc to close — standard modal expectation.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Gated-demo path skips credential validation — the parent
    // signup account already has email/password and T&C
    // acceptance from /auth/signup. Legacy path still validates
    // the form fields for any pre-gate demo records.
    if (!hasParentSignup) {
      // Client-side validation that matches the backend so the user
      // sees instant feedback instead of waiting on a round-trip.
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError("Enter a valid email address.");
        return;
      }
      if (password.length < 10) {
        setError("Password must be at least 10 characters.");
        return;
      }
      if (password !== confirm) {
        setError("Passwords don't match.");
        return;
      }
      if (!agreedToTerms) {
        setError("Please agree to the Terms and Privacy Policy to continue.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const res = await authedFetch(`${API_BASE}/demo/convert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Gated-demo: the parent account supplies everything else on
        // the backend. We always send keepSampleData:false — the demo's
        // sample records are fake and shouldn't seed the real CRM, which
        // starts clean. (Backend follow-up: ensure /demo/convert honors
        // this and provisions a fresh CRM rather than re-skinning the
        // demo's data.)
        body: JSON.stringify(hasParentSignup
          ? { keepSampleData: false }
          : {
              email: email.trim().toLowerCase(),
              password,
              name: name.trim() || undefined,
              keepSampleData: false,
              agreedToTerms,
            }),
      });
      const body = await res.json();
      if (!res.ok || !body?.success) {
        throw new Error(body?.message || "Conversion failed.");
      }

      // Re-dispatch the auth state with the new (real-account) tokens
      // and identity. Same shape as the launcher uses post-/demo/start.
      const { accessToken, refreshToken, user } = body.data || {};
      dispatch(
        signInSuccess({
          ...user,
          token: accessToken,
          refreshToken,
        })
      );

      // They've converted — drop the "resume my demo" breadcrumb so the
      // next login routes them to plan-pick / their CRM instead of back
      // into a tour.
      clearDemoMembership();

      // Carry the vertical through. demo_started records it and this did
      // not, so the funnel could tell you how many tours started per CRM and
      // how many converted overall, but never which CRM actually sells —
      // which is the one question per-vertical attribution exists to answer.
      // No userId: this leaves the app to a third-party analytics provider,
      // and a persistent user identifier is exactly what those providers'
      // terms prohibit. Sentry still gets it in the breadcrumb, first-party.
      track("demo_converted", {
        vertical: readDemoVertical(),
        hadParentSignup: !!hasParentSignup,
      });
      toast.success(hasParentSignup
        ? "You're all set — pick a plan to spin up your own CRM."
        : "Account saved! Now pick a plan to spin up your own CRM.");
      // Send them to /pricing to checkout. Their account is real and
      // their dashboard data is preserved if they bounce back.
      navigate("/pricing");
      onClose();
    } catch (err) {
      setError(err.message || "Something went wrong.");
      track("demo_convert_failed", { error: String(err.message || err) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-convert-title"
      className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 py-8"
      onClick={onClose}
    >
      <div
        className="bg-[#141b2b] border border-white/10 rounded-2xl max-w-md w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative p-6 pb-4 border-b border-white/5">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/5"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-[#1ec8c8]/15 border border-[#1ec8c8]/30 flex items-center justify-center flex-shrink-0">
              <Rocket className="w-5 h-5 text-[#1ec8c8]" />
            </div>
            <h2 id="demo-convert-title" className="text-white text-lg font-bold">
              {hasParentSignup
                ? "Pick a plan to get your own CRM"
                : "Create your real CRM"}
            </h2>
          </div>
          <p className="text-gray-400 text-sm">
            {hasParentSignup
              ? "You're already signed up — choose a plan on the next page and we'll set you up with your own Stacked CRM: a clean slate for your real contacts, properties, and deals. The demo was just a tour, so its sample data stays behind."
              : "Confirm the email and password you signed up with, then pick a plan. We'll set up your own Stacked CRM — a clean slate for your real data. The demo was just a tour, so its sample data stays behind."}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-3">
          {/* Legacy credential block — only when there's no parent
              signup account on this demo. Under the gated flow the
              parent already has email/password/T&C from /auth/signup
              and these fields are redundant. */}
          {!hasParentSignup && (
            <>
              <Field
                icon={UserIcon}
                label="Your name (optional)"
                value={name}
                onChange={setName}
                placeholder="Jane Smith"
              />
              <Field
                icon={Mail}
                label="Email"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@yourcompany.com"
                required
                inputRef={emailRef}
              />
              <Field
                icon={Lock}
                label="Password"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="At least 10 characters"
                required
                inputRef={passwordRef}
              />
              <Field
                icon={Lock}
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={setConfirm}
                placeholder="Type it again"
                required
              />
            </>
          )}

          {/* No "keep sample data" option: the demo is a tour of fake
              sample records, not the visitor's own data, so there's
              nothing of theirs to carry over. Conversion always starts
              their real CRM clean. */}

          {/* Terms + Privacy agreement — required for legacy path
              only. Gated-flow visitors accepted T&C at /auth/signup
              and the parent account carries that provenance; asking
              again here would be redundant and friction. */}
          {!hasParentSignup && (
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-white/20 bg-[#0f1421] text-[#1ec8c8] focus:ring-[#1ec8c8] focus:ring-offset-0"
              />
              <span className="text-xs text-gray-300 leading-snug">
                I agree to the{" "}
                <a
                  href="/terms-and-conditions-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1ec8c8] hover:underline"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#1ec8c8] hover:underline"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#1ec8c8] hover:bg-[#34b8b3] text-black font-semibold transition-all disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {hasParentSignup ? "Continuing…" : "Saving…"}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                {hasParentSignup ? "Continue to plans" : "Save my account & pick a plan"}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          {/* Trust strip */}
          <div className="flex items-center justify-center gap-3 text-[11px] text-gray-500 pt-1">
            <span className="inline-flex items-center gap-1">
              <Check className="w-3 h-3 text-emerald-400" />
              No card yet
            </span>
            <span className="inline-flex items-center gap-1">
              <Check className="w-3 h-3 text-emerald-400" />
              Fresh, clean CRM
            </span>
            <span className="inline-flex items-center gap-1">
              <Check className="w-3 h-3 text-emerald-400" />
              Cancel any time
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, onChange, placeholder, type = "text", required, inputRef }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
        {label}
      </span>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="w-full pl-9 pr-3 py-2.5 bg-[var(--lighter-dark)] border border-white/10 rounded-lg text-sm text-white placeholder-gray-500 focus:border-[#1ec8c8]/60 focus:ring-2 focus:ring-[#1ec8c8]/30 focus:outline-none"
        />
      </div>
    </label>
  );
}
